import { ReactElement, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { ctxUser } from '@/contexts/UserContext';
import { useNotificationStore } from '@/stores/notificationstore';
import TrackingResponseHandler from './components/tracking/TrackingResponseHandler';
import { PlutoClientException } from './PlutoClientException';
import { getUser } from '@/tmp/UserService';

type ServerError = {
  localizedMessage: string;
  message: string;
};

export function HandleErrors({ children, logout }: { children: ReactElement; logout?: (explicit: boolean) => void }) {
  const { t } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const userCtx = useContext(ctxUser);
  const trackingResponseHandler = new TrackingResponseHandler();
  const messagesResponseHandler = new TrackingResponseHandler();
  const trackingNetworkHandler = new TrackingResponseHandler();

  function getServerSideError(response: AxiosResponse): ServerError | undefined {
    const serverSideErrorData = response?.data as ServerError;
    const localizedMessage = serverSideErrorData?.localizedMessage;
    if (localizedMessage) {
      return serverSideErrorData;
    }
    return undefined;
  }

  function createClientExceptionByAxiosError(error: AxiosError): PlutoClientException | undefined | null {
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      return new PlutoClientException(t('error.api.err_timeout'), error.request);
    }

    if (error.code === 'ECONNABORTED' && error.message.includes('Request aborted')) {
      return null;
    }

    if (!navigator.onLine) {
      return new PlutoClientException(t('error.api.err_network'), error.request);
    }

    if (error.response) {
      const serverSideError = getServerSideError(error.response);
      if (serverSideError !== undefined) {
        if (serverSideError.message) {
          console.warn(serverSideError.message);
        }
        return new PlutoClientException(serverSideError.localizedMessage, error.request);
      }

      const statusCode = error.response.status;
      if (statusCode == 403) {
        return new PlutoClientException(t('error.api.err_forbidden'), error.request);
      }

      return new PlutoClientException(t('error.api.err_network'), error.request);
    }

    if (error.code === 'ERR_NETWORK' || error.message === 'Network error') {
      const anyTrackingUrl = error.config?.url?.includes('tracking');
      if (!anyTrackingUrl) {
        return new PlutoClientException(t('error.api.err_network'), error.request);
      }
      const handled = trackingNetworkHandler.handleFailedRequest();
      if (!handled) {
        return new PlutoClientException(t('error.api.err_network'), error.request);
      } else {
        return null;
      }
    }

    return undefined;
  }

  const handleTrackingError = useCallback(
    (error: AxiosError) => {
      const isTrackingUrl = error.config?.url?.includes('positions/tracking');
      if (!isTrackingUrl) {
        return false;
      }
      if (!error.response) {
        // errors without response like ERR_INTERNET_DISCONNECTED
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          const handled = trackingResponseHandler.handleFailedRequest();
          if (!handled) {
            showNotification('error', t('error.api.err_timeout'));
          }
          return true;
        }
        return false;
      }
      const status = error.response.status;
      if (status >= 400 && status < 500) {
        return false;
      }
      const errorHandled = trackingResponseHandler.handleFailedRequest();
      if (!errorHandled) {
        showNotification('error', t('error.api.failed_tracking'));
      }
      return true;
    },
    [t, showNotification]
  );

  const handleMessagesError = useCallback(
    (error: AxiosError) => {
      const isTrackingUrl = error.config?.url?.includes('messages/tracking');
      if (!isTrackingUrl) {
        return false;
      }
      if (!error.response) {
        // errors without response like ERR_INTERNET_DISCONNECTED
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          const handled = messagesResponseHandler.handleFailedRequest();
          if (!handled) {
            showNotification('error', t('error.api.err_timeout'));
          }
          return true;
        }
        return false;
      }
      const status = error.response.status;
      if (status >= 400 && status < 500) {
        return false;
      }
      const errorHandled = messagesResponseHandler.handleFailedRequest();
      if (!errorHandled) {
        showNotification('error', t('error.api.failed_messages'));
      }
      return true;
    },
    [t, showNotification]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function interceptorErrorHandler(error: any): Promise<unknown> {
    if (error.code === 'ERR_CANCELED') {
      return Promise.resolve({ status: 499 });
    }

    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      const statusCode = error.response.status;

      if (statusCode === 401) {
        // a getCurrentUser es a login lekezeli a 401-et sajat maga
        const isLoginUrl =
          error.response.config.url?.includes('login') || error.response.config.url?.includes('current');
        if (!isLoginUrl) {
          if (userCtx?.isToken()) {
            // ha tokenes es lejart a session-je a pluto-serverben
            // a reload utan megprobal ujra bejelentkezni
            // ha meg jo a token akkor minden megy tovabb
            // ha lejart a token akkor hibauzenetet kap
            window.location.reload();
          } else {
            if (error.config) {
              // ha lejart a pluto serverben a session de van remember-me cookie
              // akkor automatikusan ujraindithatjuk egy keressel
              // FONTOS: a getUser nem mehet erre az agra mert akkor vegtelen ciklus lesz !!!!!
              // a getUser nem jon be ide mert isLoginUrl
              const response = await getUser();
              if (response.status === 200) {
                // van user, minden mehet tovabb
                return axios(error.config);
              }
            }
            logout?.(false);
          }
        }
        return Promise.reject(error);
      }
    }

    if (handleTrackingError(error)) {
      return Promise.reject(error);
    }

    if (handleMessagesError(error)) {
      return Promise.reject(error);
    }

    const clientException = createClientExceptionByAxiosError(error);

    if (clientException === null) {
      return Promise.reject(error);
    }

    if (clientException === undefined) {
      console.error('unknown error: ', error);
      showNotification('error', t('error.api.unknown'));
      return Promise.reject(error);
    }

    if (clientException.statusCode !== 403) {
      showNotification('error', clientException.message);
    }

    return Promise.reject(clientException);
  }

  useEffect(() => {
    axios.interceptors.request.use((config) => {
      const params = new URLSearchParams(document.location.search);
      const l = params.get('l');
      const headerLanguage = l || localStorage.getItem('language');
      if (headerLanguage) {
        config.headers['Accept-language'] = headerLanguage;
      }
      config.timeout = 60000;
      return config;
    }, interceptorErrorHandler);

    axios.interceptors.response.use(function (response) {
      const isTrackingUrl = response?.config?.url?.includes('positions/tracking');
      if (isTrackingUrl) {
        trackingResponseHandler.successfulRequest();
      }
      const isMessagesUrl = response?.config?.url?.includes('messages/tracking');
      if (isMessagesUrl) {
        messagesResponseHandler.successfulRequest();
      }
      const isAnyTrackingUrl = response?.config?.url?.includes('tracking');
      if (isAnyTrackingUrl) {
        trackingNetworkHandler.successfulRequest();
      }
      return response;
    }, interceptorErrorHandler);

    return () => {
      axios.interceptors.request.clear();
      axios.interceptors.response.clear();
    };
  }, [t, showNotification, logout]);

  return children;
}
